import React, { useState, useEffect, useContext } from 'react';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { toast } from 'react-toastify';
import { AuthContext } from '../context/AuthContext';
import { usePermission } from '../context/PermissionContext';
import { Trash, FileText, Filter, RefreshCw, User, Calendar, Info, Trash2 } from 'lucide-react';
import { getLogs, getLogsByAction, getDeletionLogs, clearAllLogs } from '../api/logApi';

// Log temizleme onay modalı bileşeni
const LogClearConfirmModal = ({ onClose, onConfirm, isLoading }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="flex items-center justify-center mb-4 text-red-600">
          <Trash2 size={48} />
        </div>
        <h3 className="text-xl font-bold text-center mb-2">Tüm Logları Temizle</h3>
        <p className="text-gray-600 text-center mb-6">
          Bu işlem tüm sistem loglarını kalıcı olarak silecektir. Bu işlem geri alınamaz.
          Devam etmek istediğinize emin misiniz?
        </p>
        <div className="flex justify-center space-x-4">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400"
          >
            İptal
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-400 flex items-center"
          >
            {isLoading ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Temizleniyor...
              </>
            ) : (
              <>
                <Trash2 size={16} className="mr-2" />
                Tüm Logları Temizle
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// Sistem Logları Sayfası - Sadece admin erişimi
const LogsPage = () => {
  const { user } = useContext(AuthContext);
  const { hasPermission } = usePermission();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'delete', veya belirli bir eylem
  const [searchTerm, setSearchTerm] = useState('');
  const [showLogClearModal, setShowLogClearModal] = useState(false);
  const [logClearing, setLogClearing] = useState(false);
  
  // Log verilerini getir
  useEffect(() => {
    const fetchLogs = async () => {
      setLoading(true);
      try {
        let data;
        
        // Filtre uygulanmışsa
        if (filter === 'delete') {
          data = await getDeletionLogs();
        } else if (filter !== 'all') {
          data = await getLogsByAction(filter);
        } else {
          data = await getLogs();
        }
        
        setLogs(data);
      } catch (error) {
        console.error('Log getirme hatası:', error);
        toast.error('Loglar yüklenirken bir hata oluştu');
      } finally {
        setLoading(false);
      }
    };
    
    fetchLogs();
  }, [filter]);
  
  // Tüm logları temizle
  const handleClearLogs = async () => {
    setLogClearing(true);
    try {
      const result = await clearAllLogs();
      toast.success(`Loglar başarıyla temizlendi. Silinen kayıt sayısı: ${result.count}`);
      setShowLogClearModal(false);
      // Loglar temizlendikten sonra, yeniden yükle
      const freshLogs = await getLogs();
      setLogs(freshLogs);
    } catch (error) {
      toast.error(`Log temizleme hatası: ${error.message}`);
    } finally {
      setLogClearing(false);
    }
  };
  
  // Filtreleme ve arama işlemleri
  const filteredLogs = logs.filter(log => {
    // Eğer arama terimi varsa
    if (searchTerm) {
      return (
        (log.username && log.username.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (log.action && log.action.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (log.details && log.details.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    return true;
  });
  
  // Eylem tipine göre ikon belirle
  const getActionIcon = (action) => {
    if (action.includes('delete')) {
      return <Trash size={16} className="text-red-500" />;
    } else if (action.includes('create')) {
      return <FileText size={16} className="text-green-500" />;
    } else if (action.includes('update')) {
      return <RefreshCw size={16} className="text-blue-500" />;
    } else if (action.includes('login')) {
      return <User size={16} className="text-purple-500" />;
    } else {
      return <Info size={16} className="text-gray-500" />;
    }
  };
  
  // Yetki kontrolü - artık view_logs izni gerekli
  const hasAdminAccess = hasPermission('view_logs');
  
  // Logs paneli izni yoksa erişim engellendi
  if (!hasAdminAccess) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="text-red-500 mb-4">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-gray-700">Erişim Engellendi</h2>
        <p className="text-gray-500 mt-2">Bu sayfayı görüntülemek için log görüntüleme izniniz olması gerekiyor.</p>
      </div>
    );
  }
  
  return (
    <div className="container mx-auto py-6 px-4">
      <div className="flex flex-col md:flex-row justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-4 md:mb-0">Sistem Logları</h1>
        
        {/* Log Temizleme Butonu */}
        <button
          onClick={() => setShowLogClearModal(true)}
          className="flex items-center px-4 py-2 bg-red-50 text-red-600 border border-red-200 rounded-md hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500"
        >
          <Trash2 size={16} className="mr-2" />
          Tüm Logları Temizle
        </button>
      </div>
      
      {/* Filtreleme ve Arama */}
      <div className="flex flex-col md:flex-row justify-between gap-4 mb-6">
        <div className="flex items-center">
          <div className="relative">
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="all">Tüm Loglar</option>
              <option value="delete">Silme İşlemleri</option>
              <option value="create_refund">İade Oluşturma</option>
              <option value="update_refund_status">İade Durumu Güncelleme</option>
              <option value="update_refund">İade Güncelleme</option>
              <option value="login">Kullanıcı Girişi</option>
              <option value="clear_logs">Log Temizleme</option>
            </select>
            <div className="absolute left-3 top-2.5 text-gray-400">
              <Filter size={16} />
            </div>
          </div>
        </div>
        
        <div className="relative">
          <input
            type="text"
            placeholder="Ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="px-4 py-2 pl-10 w-full md:w-64 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
          <div className="absolute left-3 top-2.5 text-gray-400">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>
      </div>
      
      {/* Log Tablosu */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
          </div>
        ) : filteredLogs.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64">
            <FileText size={48} className="text-gray-300 mb-4" />
            <p className="text-gray-500">Gösterilecek log kaydı bulunamadı</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Eylem</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Detay</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kullanıcı</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">IP Adresi</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tarih</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredLogs.map(log => (
                  <tr key={log.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getActionIcon(log.action)}
                        <span className="ml-2 text-sm text-gray-900">{log.action}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{log.details}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <User size={16} className="text-gray-400 mr-2" />
                        <span className="text-sm text-gray-900">{log.username || 'Bilinmeyen Kullanıcı'}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{log.ip_address || '-'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Calendar size={16} className="text-gray-400 mr-2" />
                        <span className="text-sm text-gray-500">
                          {log.created_at && format(new Date(log.created_at), 'dd MMM yyyy HH:mm', { locale: tr })}
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      
      {/* Bilgi Kutusu */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-md p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <Info size={20} className="text-blue-500" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">Sistem Logları Hakkında</h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>Bu sayfada sistemde gerçekleştirilen tüm işlemlerin kayıtlarını görebilirsiniz. Özellikle silme işlemleri için "Silme İşlemleri" filtresini kullanabilirsiniz.</p>
              <p className="mt-2">Tüm logları silmek için sağ üstteki "Tüm Logları Temizle" butonunu kullanabilirsiniz.</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Log Temizleme Onay Modalı */}
      {showLogClearModal && (
        <LogClearConfirmModal
          onClose={() => setShowLogClearModal(false)}
          onConfirm={handleClearLogs}
          isLoading={logClearing}
        />
      )}
    </div>
  );
};

export default LogsPage; 